import { notification } from 'antd';
import { useParams } from 'react-router';

import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import { useGetSegregationOptionsQuery } from '../../../api/globalDataApi';
import { useUpdateSampleSegregationMutation } from '../../../api/variantApi';
import { VARIANT_STAGE_TYPES } from '../../constants';
import { VariantSample } from '../../redux';

interface FormValues {
  segregation: string | undefined | null;
}

interface UseSegregationModalLogicProps {
  onClose: () => void;
  variantSamples: VariantSample[];
  selectedSample: VariantSample;
}

/**
 * Custom hook that encapsulates all the business logic for SegregationModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useSegregationModalLogic = ({
  onClose,
  variantSamples,
  selectedSample,
}: UseSegregationModalLogicProps) => {
  const params = useParams();

  // Mutation hook returns a [trigger, result] tuple.
  // After a successful save, invalidatesTags fires and RTK Query refetches the
  // samples list and stages automatically
  const [updateSampleSegregation] = useUpdateSampleSegregationMutation();

  const {
    isLoading,
    setIsLoading,
    user,
    stageInfo,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose,
    successMessage: 'Sample segregation updated successfully.',
    errorMessage: 'Failed to update segregation. Please try again.',
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  const { data: segregationOptions = [] } = useGetSegregationOptionsQuery();

  const modalOpts = {
    ...createModalOpts('Please select variant segregation for the sample'),
  };

  const initialValues: FormValues = {
    segregation: selectedSample.segregation,
  };

  const handleFormSubmit = async (values: any) => {
    if (isLoading) {
      return;
    }

    if (user && params.vid) {
      try {
        setIsLoading(true);

        await updateSampleSegregation({
          variant_id: params.vid,
          sample_id: selectedSample.sample_id,
          requestData: { segregation: values.segregation },
        }).unwrap();

        // invalidatesTags fires automatically — RTK Query refetches VariantSamples

        notification.success({
          message: 'Success!',
          description: 'Sample segregation updated successfully.',
        });
        onClose();
      } catch (error) {
        console.error(error);
        notification.error({
          message: 'Error!',
          description: 'Failed to update segregation. Please try again.',
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      displayUnAuthorizedNotification();
    }
  };

  return {
    isLoading,
    user,
    segregationOptions,
    stageInfo,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleFormSubmit,
  };
};

export default useSegregationModalLogic;
